/**
 * Auth token helpers.
 *
 * Auth0's getAccessTokenSilently only refreshes when the *access* token is near
 * expiry. This app sends the id_token as Bearer, so we must decide freshness
 * from the id_token exp claim ourselves.
 */

/** Match Auth0 SPA SDK's ~60s access-token refresh window. */
export const ID_TOKEN_EXPIRY_BUFFER_SECONDS = 60

/**
 * Whether an id_token JWT is still usable as an API Bearer (not within the
 * refresh buffer of exp). Decodes the payload only — does not verify the
 * signature (Auth0 already did).
 * @param {string|null|undefined} idToken
 * @returns {boolean}
 */
export function isIdTokenUsable(idToken) {
  const exp = getIdTokenExp(idToken)
  if (exp == null) return false
  return exp > _nowSeconds() + ID_TOKEN_EXPIRY_BUFFER_SECONDS
}

/**
 * @param {string|null|undefined} idToken
 * @returns {number|null} exp claim in seconds, or null if unreadable
 */
export function getIdTokenExp(idToken) {
  const exp = _decodeJwtPayload(idToken)?.exp
  return typeof exp === 'number' ? exp : null
}

/**
 * Session and timing details from id_token claims for auth-flow analytics.
 * Omits any field the claims cannot provide.
 * @param {Object} claims - Decoded id_token claims
 * @returns {Object}
 */
export function getTokenDetailsFromClaims(claims) {
  const { sid, exp, iat } = claims || {}
  const details = {}

  if (sid) details.auth0_session_id = sid
  if (typeof exp === 'number') details.token_expires_in_s = exp - _nowSeconds()
  if (typeof exp === 'number' && typeof iat === 'number') details.id_token_lifetime_s = exp - iat
  return details
}

/**
 * Token details for auth-flow analytics, read from a raw id_token.
 * @param {string|null|undefined} idToken
 * @returns {Object} Empty when the token is unreadable
 */
export function getIdTokenDetails(idToken) {
  const payload = _decodeJwtPayload(idToken)
  if (!payload) return {}

  const details = getTokenDetailsFromClaims(payload)
  if (payload.iss) details.token_issuer = payload.iss
  if (payload.aud) details.token_audience = payload.aud
  return details
}

function _nowSeconds() {
  return Math.floor(Date.now() / 1000)
}

/**
 * @param {string|null|undefined} token
 * @returns {Object|null} JWT payload, or null if unreadable
 */
function _decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null

  try {
    const payloadSegment = token.split('.')[1]
    if (!payloadSegment) return null

    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(normalized))
    return payload && typeof payload === 'object' ? payload : null
  } catch {
    return null
  }
}
